import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Table, Params, Actions, Menu, Merge } from 'nallian-table-builder-ui';
import { PageContent, PageTitle, Link, Button } from 'nallian-shared-ui/lib/components';
import HasSomeRights from 'nallian-shared-ui/lib/layout/rights/HasSomeRights';
import { addParametersToUri } from 'nallian-shared-ui/lib/lib/uri';
import { TABS, INFINITE_SCROLL, ORGANISATIONS_RESOURCE } from '../constants';
import ActiveState from './ActiveState';
import apiRoutes from '../../../app/api/routes';
import ConditionalOrganisationMenu from './table/ConditionalOrganisationMenu';
import TypeToConfirmModal from '../../app/components/TypeToConfirmModal';
import RemoveOrganisationService from '../services/organisation/removeOrganisationService';
import CountryFilter from './organisations/filter/CountryFilter';
import Country from '../../app/components/Country';
import ActiveStateFilter from './organisations/filter/ActiveStateFilter';
import IdentificationCodes from './organisations/table/IdentificationCodes';
import IdentificationCodeFilter from './organisations/filter/IdentificationCodeFilter';
import OrgLogo from './table/OrgLogo';

const FILTERED = { hasApplyButton: true };

const ORGANISATION_RIGHTS = [
  'ORGANISATIONS.MANAGE.ALL',
  'ORGANISATIONS.MANAGE.ALL.EXCEPTSYSTEMORGS',
];

const OrganisationsOverview = ({ dispatchRemoveOrganisationDelete, removeOrganisationFetch }) => {
  const { t } = useTranslation();
  const [removalItem, setRemovalItem] = useState(null);

  const handleCancel = useCallback(() => setRemovalItem(null), [setRemovalItem]);
  const handleSetRemovalItem = useCallback((_, item) => setRemovalItem(item), [setRemovalItem]);

  const handleRemove = useCallback(() => {
    dispatchRemoveOrganisationDelete(removalItem.id, () => setRemovalItem(null), t);
  }, [removalItem, dispatchRemoveOrganisationDelete, t]);

  return (
    <>
      <PageTitle>{t('organisationsOverview')}</PageTitle>
      <PageContent>
        <HasSomeRights rights={ORGANISATION_RIGHTS}>
          <Link to="/organisations/new">
            <Button btnprimary icon="plus" content={t('createNewOrganisation')} />
          </Link>
        </HasSomeRights>
        <Table
          infiniteScroll={INFINITE_SCROLL}
          resource={ORGANISATIONS_RESOURCE}
          url={addParametersToUri(apiRoutes.v2Organisations())}
          pageSize={50}
          noUserSettings
          resizable
          sorted
          filtered={FILTERED}
        >
          <Actions.Link
            width="2.5rem"
            align="center"
            icon="eye"
            url={`/organisations/:id/${TABS.DETAILS}`}
          />
          <Params.String label="organisationName" name="name" width="16rem" minWidth="16rem" grow />
          <Params.Custom
            label="logo"
            name="logoUrl"
            width="8rem"
            minWidth="8rem"
            content={OrgLogo}
          />
          <Params.String name="city" label="city" width="10rem" minWidth="10rem" grow />
          <Params.Custom
            name="countryCode"
            label="country"
            sortName="countryCode"
            content={Country}
            filter={CountryFilter}
            width="5rem"
            minWidth="5rem"
            grow
          />
          <Params.Custom
            name="identificationCodes"
            content={IdentificationCodes}
            filter={IdentificationCodeFilter}
            width="8rem"
            minWidth="8rem"
            noSort
            grow
          />
          <Params.Custom
            content={ActiveState}
            filter={ActiveStateFilter}
            name="activeState"
            width="8rem"
            minWidth="8rem"
            noSort
            grow
          />
          <Merge
            width="4.5rem"
            align="center"
            type="Menu"
            as={ConditionalOrganisationMenu}
            hasSomeRights={ORGANISATION_RIGHTS}
          >
            <Menu width="3.5rem" align="center">
              <Actions.Button
                icon="trash"
                label={t('removeOrganisation')}
                content={t('removeOrganisation')}
                onClick={handleSetRemovalItem}
              />
            </Menu>
          </Merge>
        </Table>
        {removalItem && (
          <TypeToConfirmModal
            onConfirm={handleRemove}
            onCancel={handleCancel}
            header={t('app:areYouSureRemoveOrganisation', { item: removalItem })}
            loading={removeOrganisationFetch.pending}
            value={removalItem?.name}
          />
        )}
      </PageContent>
    </>
  );
};

OrganisationsOverview.propTypes = {
  dispatchRemoveOrganisationDelete: PropTypes.func.isRequired,
  removeOrganisationFetch: PropTypes.object.isRequired,
};

export default RemoveOrganisationService(OrganisationsOverview);
